import React, { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { restorePurchases } from '../services/payment';
import { Lock, Check, X, Sparkles, Award } from 'lucide-react';

const PaywallModal = ({ onUpgrade, onClose, onRestored }) => {
  const [restoring, setRestoring] = useState(false);
  const [restoreMsg, setRestoreMsg] = useState('');

  const handleRestore = async () => {
    setRestoring(true);
    setRestoreMsg('');
    try {
      const restored = await restorePurchases();
      if (restored) {
        setRestoreMsg('Purchase restored successfully!');
        setTimeout(() => { onRestored && onRestored(); onClose(); }, 1500);
      } else {
        setRestoreMsg('No previous purchase found.');
      }
    } catch (e) {
      setRestoreMsg('Restore failed. Please try again.');
    }
    setRestoring(false);
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
        
        <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors z-10">
          <X className="w-6 h-6 text-gray-600" />
        </button>

        <div className="text-center p-8 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white rounded-t-3xl">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl mb-4">
            <Lock className="w-10 h-10" />
          </div>
          <h2 className="text-3xl font-bold mb-2">Unlock Your Full Career Profile</h2>
          <p className="text-white/90 text-lg">Get complete insights, vision board, and personalized guidance</p>
        </div>

        <div className="p-8">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Premium Features Include:</h3>
          
          <div className="space-y-4 mb-8">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Full Detailed Profile Report</h4>
                <p className="text-sm text-gray-600">Complete analysis with strengths, growth areas, and traits</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">All 12 Industry-Specific Careers</h4>
                <p className="text-sm text-gray-600">Personalized career paths matched to your profile</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Interactive Vision Board</h4>
                <p className="text-sm text-gray-600">Visual representation of your goals with inspiring imagery</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Downloadable PDF Report</h4>
                <p className="text-sm text-gray-600">Professional printable report you can save and share</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Email Sharing & Social Features</h4>
                <p className="text-sm text-gray-600">Share your results with mentors and friends</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Lifetime Access</h4>
                <p className="text-sm text-gray-600">Return anytime to view your results</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-600">One-time payment</p>
                <div className="flex items-baseline space-x-2">
                  <span className="text-4xl font-bold text-gray-900">$9.99</span>
                  <span className="text-gray-500 line-through">$29.99</span>
                </div>
              </div>
              <div className="bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold">
                67% OFF
              </div>
            </div>
            <p className="text-xs text-gray-600">🎉 Limited time offer - Regular price $29.99</p>
          </div>

          <button
            onClick={onUpgrade}
            className="w-full py-5 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 text-white text-xl font-bold rounded-2xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all flex items-center justify-center space-x-2"
          >
            <Sparkles className="w-6 h-6" />
            <span>Unlock Premium Now - $9.99</span>
          </button>

          <button
            onClick={handleRestore}
            disabled={restoring}
            className="w-full mt-4 py-3 bg-transparent border border-gray-300 text-gray-600 font-semibold rounded-2xl hover:bg-gray-50 transition-all text-sm"
          >
            {restoring ? 'Restoring...' : 'Restore Purchases'}
          </button>
          {restoreMsg ? <p className="text-center text-sm mt-2 text-indigo-600">{restoreMsg}</p> : null}

          <div className="mt-6 flex items-center justify-center space-x-4 text-sm text-gray-500">
            <div className="flex items-center space-x-1">
              <Lock className="w-4 h-4" />
              <span>Secure Payment</span>
            </div>
            <div className="flex items-center space-x-1">
              <Award className="w-4 h-4" />
              <span>Instant Access</span>
            </div>
          </div>

          {Capacitor.getPlatform() !== 'ios' && (<p className="text-center text-xs text-gray-500 mt-6">Powered by Stripe - Your payment information is secure and encrypted</p>)}
        </div>
      </div>
    </div>
  );
};

export default PaywallModal;